import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  FetchSchedulesPayload,
  ScheduleListItem,
  ScheduleListService,
  ScheduleSearchMode
} from 'src/app/Services/schedule-list.service';
import { isApiErrorResponse } from 'src/app/Services/api-response.util';
import { NotifyService } from 'src/app/Services/notify.service';
import { Literals } from 'src/app/literals';

@Component({
  selector: 'ze-schedule-list',
  templateUrl: './schedule-list.component.html',
  styleUrls: ['./schedule-list.component.scss']
})
export class ScheduleListComponent implements OnInit, OnDestroy {

  @ViewChild('searchFilterWrap', { static: false }) searchFilterWrap: ElementRef;

  searchQuery = '';
  /** Which field the search text applies to in the fetch payload. */
  searchMode: ScheduleSearchMode = 'suiteName';
  filterMenuOpen = false;
  /** Schedule id whose row action menu is open, if any. */
  openActionMenuScheduleId: string | null = null;

  schedules: ScheduleListItem[] = [];

  currentPage = 1;
  readonly pageSize = 10;
  totalCount = 0;
  startIndex = 0;
  hasNext = false;

  loading = false;
  errorMessage = '';

  deletingScheduleId: string | null = null;
  showDeleteConfirmModal = false;
  showDeleteWarningModal = false;
  schedulePendingDelete: ScheduleListItem | null = null;

   /** Tracks which schedule is being run-now'd (for loading state). */
  runNowScheduleId: string | null = null;
  /** Tracks which schedule is being disabled/resumed (for loading state). */
  togglingStatusScheduleId: string | null = null;


  private searchDebounceTimer: any = null;
  private routerSub: Subscription | null = null;

  constructor(
    private scheduleListService: ScheduleListService,
    private router: Router,
    private notifys: NotifyService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.restoreListState();
    this.loadSchedules();

    // Reload schedules when navigating back to this route so `count` and `previous_run_time` refresh.
    this.routerSub = this.router.events.pipe(
      filter(evt => evt instanceof NavigationEnd)
    ).subscribe(() => {
      // Only reload when we're currently on the scheduler list route.
      if (this.router && typeof this.router.url === 'string' && this.router.url.startsWith('/scheduler')) {
        this.loadSchedules();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.routerSub) {
      this.routerSub.unsubscribe();
      this.routerSub = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    if (this.filterMenuOpen) {
      const root = this.searchFilterWrap && this.searchFilterWrap.nativeElement;
      if (root && !root.contains(target)) {
        this.filterMenuOpen = false;
      }
    }

    if (this.openActionMenuScheduleId) {
      const menus = document.querySelectorAll('.row-actions-menu');
      let insideMenu = false;
      menus.forEach(menu => {
        if (menu.contains(target)) {
          insideMenu = true;
        }
      });
      if (!insideMenu) {
        this.openActionMenuScheduleId = null;
      }
    }
  }

  get searchPlaceholder(): string {
    return this.searchMode === 'suiteName'
      ? 'Search by suite name...'
      : 'Search by schedule name...';
  }

  get searchAriaLabel(): string {
    return this.searchMode === 'suiteName'
      ? 'Search schedules by suite name'
      : 'Search schedules by schedule name';
  }

  toggleFilterMenu(): void {
    this.filterMenuOpen = !this.filterMenuOpen;
    if (this.filterMenuOpen) {
      this.openActionMenuScheduleId = null;
    }
  }
  openMenuDirection: 'up' | 'down' = 'down';

  toggleActionMenu(scheduleId: string, event: MouseEvent): void {
  event.stopPropagation();
  this.filterMenuOpen = false;
  this.openActionMenuScheduleId =
    this.openActionMenuScheduleId === scheduleId ? null : scheduleId;

  if (this.openActionMenuScheduleId === scheduleId) {
    this.setMenuDirection(event);
    }
  }

  private setMenuDirection(event: MouseEvent): void {
  const target = event.currentTarget as HTMLElement;
  const menuButton = target && target instanceof HTMLElement
  ? target.closest('.row-actions-menu') as HTMLElement
  : null;
  if (!menuButton) {
    this.openMenuDirection = 'down';
    return;
  }

  const rect = menuButton.getBoundingClientRect();
  const estimatedMenuHeight = 180;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  this.openMenuDirection =
    spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow ? 'up' : 'down';
}

  isActionMenuOpen(scheduleId: string): boolean {
    return this.openActionMenuScheduleId === scheduleId;
  }

  closeActionMenu(): void {
    this.openActionMenuScheduleId = null;
  }

  setSearchMode(mode: ScheduleSearchMode): void {
    if (this.searchMode !== mode) {
      this.searchMode = mode;
      this.currentPage = 1;
      // this.loadSchedules();
    }
    this.filterMenuOpen = false;
  }

  get totalPages(): number {
    if (!this.totalCount || !this.pageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get rangeStart(): number {
    if (!this.schedules.length) {
      return 0;
    }
    return this.startIndex + 1;
  }

  get rangeEnd(): number {
    if (!this.schedules.length) {
      return 0;
    }
    return Math.min(this.startIndex + this.schedules.length, this.totalCount);
  }

  reload(): void {
    this.loadSchedules();
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.loadSchedules();
    }, 400);
  }

  onSearchSubmit(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.currentPage = 1;
    this.loadSchedules();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadSchedules();
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.hasNext) {
      this.goToPage(this.currentPage + 1);
    }
  }

  trackByScheduleId(_index: number, schedule: ScheduleListItem): string {
    return schedule.id;
  }

  onEdit(schedule: ScheduleListItem): void {
    this.scheduleListService.saveListReturnState({
      page: this.currentPage,
      searchQuery: this.searchQuery,
      searchMode: this.searchMode
    });
    this.router.navigate(['/scheduler/edit-schedule', schedule.id]);
  }

  // ─── Run Now ────────────────────────────────────────────────────────────────
  onRunNow(schedule: ScheduleListItem): void {
    if (this.runNowScheduleId) {
      return;
    }
    this.runNowScheduleId = schedule.id;
    this.scheduleListService.runNow(schedule.id).subscribe({
      next: response => {
        this.runNowScheduleId = null;
        if (isApiErrorResponse(response)) {
          this.notifys.notifyApiError(Literals.Error, response, 'Failed to trigger schedule. Please try again.');
          return;
        }
        const msg = (response && response.operationMessage) || 'Schedule triggered immediately.';
        this.notifys.notify(Literals.SMALL_SUCCESS, Literals.Success, msg);
        this.loadSchedules();
      },
      error: error => {
        this.runNowScheduleId = null;
        this.notifys.notifyApiError(Literals.Error, error, 'Failed to trigger schedule. Please try again.');
      }
    });
  }
  isRunningNow(schedule: ScheduleListItem): boolean {
    return this.runNowScheduleId === schedule.id;
  }
  // ─── Disable / Resume ───────────────────────────────────────────────────────
  onToggleStatus(schedule: ScheduleListItem): void {
    if (this.togglingStatusScheduleId) {
      return;
    }
    const action: 'disable' | 'resume' = this.isStatusActive(schedule.status) ? 'disable' : 'resume';
    this.togglingStatusScheduleId = schedule.id;
    this.scheduleListService.updateScheduleStatus(schedule.id, action).subscribe({
      next: response => {
        this.togglingStatusScheduleId = null;
        if (isApiErrorResponse(response)) {
          const verb = action === 'disable' ? 'disable' : 'resume';
          this.notifys.notifyApiError(Literals.Error, response, `Failed to ${verb} schedule. Please try again.`);
          return;
        }
        const msg = (response && response.operationMessage)
          || (action === 'disable' ? 'Schedule disabled.' : 'Schedule resumed.');
        this.notifys.notify(Literals.SMALL_SUCCESS, Literals.Success, msg);
        this.loadSchedules();
      },
      error: error => {
        this.togglingStatusScheduleId = null;
        const verb = action === 'disable' ? 'disable' : 'resume';
        this.notifys.notifyApiError(Literals.Error, error, `Failed to ${verb} schedule. Please try again.`);
      }
    });
  }
  isTogglingStatus(schedule: ScheduleListItem): boolean {
    return this.togglingStatusScheduleId === schedule.id;
  }

  // ─── Delete ─────────────────────────────────────────────────────────────────
  onDelete(schedule: ScheduleListItem): void {
    if (this.deletingScheduleId) {
      return;
    }

    this.deletingScheduleId = schedule.id;

    this.scheduleListService.getCancellationWindow(schedule.id).subscribe({
      next: response => {
        if (isApiErrorResponse(response) || response.data == null) {
          this.deletingScheduleId = null;
          this.notifys.notifyApiError('Error', response, 'Unable to verify cancellation window. Please try again.');
          return;
        }

        const cancellationValue = Number(response.data);
        this.deletingScheduleId = null;
        this.schedulePendingDelete = schedule;

        if (this.canDeleteWithoutWarning(cancellationValue)) {
          this.showDeleteConfirmModal = true;
        } else {
          this.showDeleteWarningModal = true;
        }
      },
      error: error => {
        this.deletingScheduleId = null;
        this.notifys.notifyApiError('Error', error, 'Unable to verify cancellation window. Please try again.');
      }
    });
  }

  confirmDelete(): void {
    const schedule = this.schedulePendingDelete;
    this.cancelDelete();
    if (schedule) {
      this.executeDelete(schedule);
    }
  }

  cancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.schedulePendingDelete = null;
  }

  confirmDeleteWithWarning(): void {
    const schedule = this.schedulePendingDelete;
    this.cancelDeleteWarning();
    if (schedule) {
      this.executeDelete(schedule);
    }
  }

  cancelDeleteWarning(): void {
    this.showDeleteWarningModal = false;
    this.schedulePendingDelete = null;
  }

  isDeleting(schedule: ScheduleListItem): boolean {
    return this.deletingScheduleId === schedule.id;
  }

  /** -1 or 1 from checkCancellationWindow: delete without confirmation popup. */
  private canDeleteWithoutWarning(cancellationValue: number): boolean {
    return cancellationValue === -1 || cancellationValue === 1;
  }

  private executeDelete(schedule: ScheduleListItem): void {
    this.deletingScheduleId = schedule.id;

    this.scheduleListService.deleteSchedule(schedule.id).subscribe({
      next: response => {
        this.deletingScheduleId = null;
        if (isApiErrorResponse(response)) {
          this.notifys.notifyApiError(Literals.Error, response, 'Failed to delete schedule. Please try again.');
          return;
        }
        const message = (response && response.message)
          || (response && response.operationMessage)
          || 'Schedule deleted successfully.';
        this.notifys.notify(Literals.SMALL_SUCCESS, Literals.Success, message);
        this.loadSchedules();
      },
      error: error => {
        this.deletingScheduleId = null;
        this.notifys.notifyApiError(Literals.Error, error, 'Failed to delete schedule. Please try again.');
      }
    });
  }

  	
  // ─── Status helpers ──────────────────────────────────────────────────────────
  /**
   * Returns the display label for a schedule status.
   * Backend values: ACTIVE | DISABLED | COMPLETED
   */
  getStatusLabel(status: string): string {
    const s = this.normalizeScheduleStatus(status);

    if (s === 'DISABLED') return 'Disabled';
    if (s === 'COMPLETED') return 'Completed';
    if (s === 'ACCESS_REVOKED') return 'Access Off';
    return 'Active';
  }

  isStatusActive(status: string): boolean {
    return this.normalizeScheduleStatus(status) === 'ACTIVE';
  }

  isStatusDisabled(status: string): boolean {
    const s = this.normalizeScheduleStatus(status);
    return s === 'DISABLED' || s === 'ACCESS_REVOKED';
  }
  isStatusCompleted(status: string): boolean {
    return this.normalizeScheduleStatus(status) === 'COMPLETED';
  }
  isStatusAccessRevoked(status: string): boolean {
    return this.normalizeScheduleStatus(status) === 'ACCESS_REVOKED';
  }
  /** Legacy paused kept for backward compat with older backend data. */

  isStatusPaused(status: string): boolean {
    return this.normalizeScheduleStatus(status) === 'PAUSED';
  }

  private normalizeScheduleStatus(status: string): string {
    if (!status) {
      return 'ACTIVE';
    }
    return String(status).trim().toUpperCase();
  }

  /**
   * Returns triggered count preferring the top-level field first,
   * then falling back to the nested recurrence field.
   */
  getTriggeredCount(schedule: ScheduleListItem): number | string {
    if (schedule.triggered_count != null) {
      return schedule.triggered_count;
    }
    const recurrence = (schedule as any).recurrence;
    if (recurrence && recurrence.triggered_count != null) {
      return recurrence.triggered_count;
    }
    return '—';
  }

  formatRunTime(timestamp?: number): string {
    if (timestamp === null || timestamp === undefined) {
      return '—';
    }
    // If backend uses 0 explicitly, show '0' rather than formatting epoch 1970.
    if (timestamp === 0) {
      return '0';
    }
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (date.toDateString() === now.toDateString()) {
      return `Today ${time}`;
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow ${time}`;
    }
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRecurrenceLabel(schedule: ScheduleListItem): string {
    if (schedule.human_summary) {
      return schedule.human_summary;
    }
    const recurrence = schedule.recurrence;
    const repeat = (recurrence && recurrence.repeat_type) || 'Schedule';
    const time = (recurrence && recurrence.start_time) || '';
    return time ? `${repeat} • ${time}` : repeat;
  }

  formatPreviousRunTime(schedule: ScheduleListItem): string {
    const recurrence = schedule.recurrence || {} as any;
    const rootPrevious = typeof schedule.previous_run_time === 'number' ? schedule.previous_run_time : undefined;
    const recurrencePrevious = typeof recurrence.previous_run_time === 'number' ? recurrence.previous_run_time : undefined;
    const timestamp = rootPrevious && rootPrevious > 0 ? rootPrevious
      : recurrencePrevious && recurrencePrevious > 0 ? recurrencePrevious
      : undefined;

    if (!timestamp) {
      return '—';
    }

    const timezone = recurrence.timezone || undefined;
    return this.formatPreviousRunTimestamp(timestamp, timezone);
  }

  private formatPreviousRunTimestamp(timestamp: number, timezone?: string): string {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '—';
 
    const optionsBase: Intl.DateTimeFormatOptions = {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: timezone || undefined
    };
    const timeString = new Intl.DateTimeFormat('en-US', optionsBase).format(date);
 
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric', month: 'short', day: '2-digit',
      timeZone: timezone || undefined
    };
    const dateString = new Intl.DateTimeFormat('en-US', dateOptions).format(date);
 
    const now = new Date();
    const makeParts = (d: Date) => new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      timeZone: timezone || undefined
    }).formatToParts(d);
 
    const nowParts = makeParts(now);
    const dateParts = makeParts(date);
 
    const get = (parts: Intl.DateTimeFormatPart[], type: string) =>
      (parts.find(p => p.type === type) || { value: undefined }).value;
 
    if (get(nowParts, 'year') === get(dateParts, 'year') &&
        get(nowParts, 'month') === get(dateParts, 'month') &&
        get(nowParts, 'day') === get(dateParts, 'day')) {
      return `Today ${timeString}`;
    }
 
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesParts = makeParts(yesterday);
 
    if (get(yesParts, 'year') === get(dateParts, 'year') &&
        get(yesParts, 'month') === get(dateParts, 'month') &&
        get(yesParts, 'day') === get(dateParts, 'day')) {
      return `Yesterday ${timeString}`;
    }
 
    return `${dateString}, ${timeString}`;
  }

  private loadSchedules(): void {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.errorMessage = 'No project selected. Open a project to view schedules.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const payload = this.buildFetchPayload(projectId);

    this.scheduleListService.fetchSchedules(payload).subscribe({
      next: response => this.handleSchedulesResponse(response),
      error: error => this.handleLoadError(error)
    });
  }

  private buildFetchPayload(projectId: string): FetchSchedulesPayload {
    const payload: FetchSchedulesPayload = {
      projectId,
      page: String(this.currentPage),
      maxCount: String(this.pageSize)
    };

    const q = this.searchQuery.trim();
    if (q) {
      if (this.searchMode === 'suiteName') {
        payload.suiteName = q;
      } else {
        payload.scheduleName = q;
      }
    }

    return payload;
  }

  private handleSchedulesResponse(response: any): void {
    this.loading = false;
    if (!response || response.operationStatus !== Literals.CAPITAL_SUCCESS) {
      this.errorMessage = this.notifys.getApiErrorText(response, 'Failed to load schedules.');
      this.schedules = [];
      this.totalCount = 0;
      this.hasNext = false;
      return;
    }

    const pageData = response.data || {};
    this.schedules = pageData.data || [];
    this.totalCount = pageData.totalCount || 0;
    this.startIndex = pageData.startIndex || 0;
    this.hasNext = !!pageData.hasNext;

    // After deleting the last item(s) on a page, step back to the previous valid page.
    const maxPage = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    if (!this.schedules.length && this.currentPage > maxPage) {
      this.currentPage = maxPage;
      this.loadSchedules();
    }

  }

  private handleLoadError(error?: any): void {
    this.loading = false;
    this.errorMessage = this.notifys.getApiErrorText(error, 'Unable to load schedules. Please try again.');
    this.schedules = [];
    this.totalCount = 0;
    this.hasNext = false;
  }

  private restoreListState(): void {
    const saved = this.scheduleListService.consumeListReturnState();
    if (!saved) {
      return;
    }
    if (saved.page && saved.page > 0) {
      this.currentPage = saved.page;
    }
    if (saved.searchQuery) {
      this.searchQuery = saved.searchQuery;
    }
    if (saved.searchMode === 'suiteName' || saved.searchMode === 'scheduleName') {
      this.searchMode = saved.searchMode;
    }
  }
}
